import type { RootState } from '@generalTypes/rootStateTypes';
import { callPersistSnapshot } from '@newStore/documentApi/documentApiDataAccess';
import { extractContentAndRelations } from '@newStore/documentApi/documentApiHelpers';
import { refetchDocument } from '@newStore/documentApi/documentApiState';
import { NodeType } from '@nodeTypeConfig/configTypes';
import type { PayloadAction } from '@reduxjs/toolkit';
import { saveDocumentAction } from '@store/actions/documentActions';
import { addNotificationAction } from '@store/actions/notificationActions';
import { DOCUMENT_SAVED, DOCUMENT_SAVE_FAILED } from '@store/constants/actionTypes';
import { getResourceKey } from '@store/helpers/documentHelpers';
import { call, put, select, take, takeLatest } from 'redux-saga/effects';
import { getSnapshotContent, getSnapshotsForDocument } from './documentSnapshotDataAccess';
import {
  buildSnapshotChanges,
  scopeSnapshotToDiscipline,
  selectLatestSnapshot,
  selectSnapshotContext,
} from './documentSnapshotHelpers';
import {
  fetchSnapshots,
  krcSnapshotPersistFinished,
  persistKrcSnapshot,
  prepareSnapshotChangelog,
  selectSnapshot,
  snapshotChangelogPreparationFailed,
  snapshotChangelogPrepared,
  snapshotContentFetchFailed,
  snapshotContentFetched,
  snapshotsFetched,
} from './documentSnapshotState';
import type {
  PersistKrcSnapshotPayload,
  PersistKrcSnapshotResponse,
  Snapshot,
} from './documentSnapshotTypes';

function* fetchSnapshotsSaga() {
  const { snapshotDocumentHref } = yield select(selectSnapshotContext);
  const documentKey = snapshotDocumentHref ? getResourceKey(snapshotDocumentHref) : null;
  if (!documentKey) {
    return;
  }
  try {
    const snapshots: Snapshot[] = yield call(getSnapshotsForDocument, documentKey);
    yield put(snapshotsFetched({ snapshots }));
  } catch (error) {
    console.error('Error fetching snapshots', error);
    yield put(
      addNotificationAction({
        type: 'ERROR',
        message: 'Er is een fout opgetreden bij het ophalen van de versies.',
      }),
    );
  }
}

function* selectSnapshotSaga(action: ReturnType<typeof selectSnapshot>) {
  const { version } = action.payload;
  if (version === null) {
    return;
  }
  const { currentDocumentHref, rootType, snapshotDocumentHref } =
    yield select(selectSnapshotContext);
  const documentKey = snapshotDocumentHref ? getResourceKey(snapshotDocumentHref) : null;
  if (!documentKey || !currentDocumentHref) {
    return;
  }
  try {
    const results: Awaited<ReturnType<typeof getSnapshotContent>> = yield call(
      getSnapshotContent,
      documentKey,
      version,
    );
    let { content, relations } = extractContentAndRelations(results);
    if (rootType === NodeType.KRC_CURRICULUM_DISCIPLINE) {
      ({ content, relations } = scopeSnapshotToDiscipline(
        { content, relations },
        currentDocumentHref,
      ));
    }
    yield put(snapshotContentFetched({ content, relations }));
  } catch (error) {
    console.error('Error fetching snapshot content', error);
    yield put(snapshotContentFetchFailed());
    yield put(
      addNotificationAction({
        type: 'ERROR',
        message: 'Er is een fout opgetreden bij het ophalen van de versie inhoud.',
      }),
    );
  }
}

function* prepareSnapshotChangelogSaga() {
  const { currentDocumentHref, snapshotDocumentHref } = yield select(selectSnapshotContext);
  const documentKey = snapshotDocumentHref ? getResourceKey(snapshotDocumentHref) : null;
  if (!documentKey || !currentDocumentHref) {
    yield put(snapshotChangelogPreparationFailed());
    return;
  }

  try {
    const snapshots: Snapshot[] = yield call(getSnapshotsForDocument, documentKey);
    yield put(snapshotsFetched({ snapshots }));
    const latestSnapshot = selectLatestSnapshot(snapshots);
    if (!latestSnapshot) {
      yield put(snapshotChangelogPrepared({ changelog: '<p>Eerste gepubliceerde versie.</p>' }));
      return;
    }

    // Load the latest published version into the store so both the changelog and the editor compare
    // against it. selectSnapshotSaga fetches its content and dispatches snapshotContentFetched.
    yield put(selectSnapshot({ version: latestSnapshot.version }));
    const result: { type: string } = yield take([
      snapshotContentFetched.type,
      snapshotContentFetchFailed.type,
    ]);
    if (result.type === snapshotContentFetchFailed.type) {
      yield put(snapshotChangelogPreparationFailed());
      return;
    }

    const state: RootState = yield select((rootState: RootState) => rootState);
    const changelog = buildSnapshotChanges(state, false);
    yield put(snapshotChangelogPrepared({ changelog }));
  } catch (error) {
    console.error('Error preparing snapshot changelog', error);
    yield put(snapshotChangelogPreparationFailed());
    yield put(
      addNotificationAction({
        type: 'ERROR',
        message: 'Er is een fout opgetreden bij het voorbereiden van het wijzigingslogboek.',
      }),
    );
  }
}

function* persistKrcSnapshotSaga(action: PayloadAction<PersistKrcSnapshotPayload>) {
  try {
    // Leave the compare-with-latest view as soon as the user publishes, regardless of the outcome.
    yield put(selectSnapshot({ version: null }));

    const itemsToSave: number = yield select(
      (state: RootState) => state.document.viewModel.itemsToSave,
    );

    if (itemsToSave > 0) {
      yield put(saveDocumentAction());
      const saveResult: { type: string } = yield take([DOCUMENT_SAVED, DOCUMENT_SAVE_FAILED]);
      if (saveResult.type === DOCUMENT_SAVE_FAILED) {
        yield put(
          addNotificationAction({
            type: 'ERROR',
            message:
              'De versie kon niet gepubliceerd worden omdat het document niet kon worden opgeslagen.',
          }),
        );
        return;
      }
    }

    const currentDocumentHref: string | null = yield select(
      (state: RootState) => state.documentUI.currentDocument,
    );
    if (!currentDocumentHref) {
      return;
    }
    const documentKey = getResourceKey(currentDocumentHref);

    try {
      const response: PersistKrcSnapshotResponse = yield call(
        callPersistSnapshot,
        documentKey,
        action.payload,
      );

      if (!response.snapshotCreated) {
        yield put(
          addNotificationAction({
            type: 'WARNING',
            message:
              'Er zijn geen wijzigingen sinds de vorige versie. Er werd geen nieuwe versie gepubliceerd.',
          }),
        );
        return;
      }

      const { content, relations } = response.stats;
      const statsMessage =
        `Inhoud: ${content.snapshotted} nieuw, ${content.reused} hergebruikt. ` +
        `Relaties: ${relations.snapshotted} nieuw, ${relations.reused} hergebruikt.`;
      yield put(
        addNotificationAction({
          type: 'SUCCESS',
          message: `${
            action.payload.mode === 'new'
              ? 'Nieuwe versie succesvol gepubliceerd.'
              : 'Gepubliceerde versie succesvol bijgewerkt.'
          } ${statsMessage}`,
        }),
      );
      // Reload the document (like on save) so any server-side changes from publishing are reflected.
      yield put(refetchDocument());
      // refresh the snapshot dropdown so the new version shows up
      yield put(fetchSnapshots());
    } catch (error) {
      yield put(
        addNotificationAction({
          type: 'ERROR',
          message: 'Er is een fout opgetreden bij het publiceren van de versie.',
        }),
      );
    }
  } finally {
    yield put(krcSnapshotPersistFinished());
  }
}

export function* documentSnapshotSaga() {
  yield takeLatest(fetchSnapshots.type, fetchSnapshotsSaga);
  yield takeLatest(selectSnapshot.type, selectSnapshotSaga);
  yield takeLatest(prepareSnapshotChangelog.type, prepareSnapshotChangelogSaga);
  yield takeLatest(persistKrcSnapshot.type, persistKrcSnapshotSaga);
}
